import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Loader2, ArrowLeft, Eye, Calendar, User, ExternalLink } from 'lucide-react';
import { ArticleCard } from '@/components/ArticleCard';
import { SEOHead } from '@/components/SEOHead';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  actualites: 'Actualités',
  histoire: 'Histoire',
  culture: 'Culture',
  politique: 'Politique',
  sport: 'Sport'
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/articles/${slug}`);
        setArticle(response.data);
        
        // Fetch related articles from same category
        const relatedRes = await axios.get(`${API_URL}/articles?category=${response.data.category}&limit=4`);
        setRelatedArticles(relatedRes.data.filter(a => a.slug !== slug).slice(0, 3));
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Article non trouvé');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="article-loading">
        <Loader2 className="h-8 w-8 animate-spin text-red-800" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="article-error">
        <h1 className="font-playfair text-3xl font-bold text-stone-900 mb-4">
          Article non trouvé
        </h1>
        <Link 
          to="/" 
          className="font-inter text-red-800 hover:text-red-900 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const formattedDate = article.created_at 
    ? format(new Date(article.created_at), "d MMMM yyyy 'à' HH:mm", { locale: fr })
    : '';

  // Split content into paragraphs
  const contentParagraphs = article.content.split('\n').filter(p => p.trim());

  return (
    <>
      <SEOHead 
        title={`${article.title} | VersaillesPlus`}
        description={article.excerpt || article.content.substring(0, 160)}
        image={article.image_url}
        type="article"
        publishedTime={article.created_at}
        author={article.author}
      />
      <Header />
      <div className="bg-stone-100" data-testid="article-page">
      {/* Hero Image */}
      <div className="w-full h-[40vh] lg:h-[50vh] relative">
        <img
          src={article.image_url || "https://customer-assets.emergentagent.com/job_versailles-actu/artifacts/x061u1gg_logo%20Versailles%2B.jpeg"}
          alt={article.title}
          className={article.image_url ? "w-full h-full object-cover" : "w-full h-full object-contain p-12 bg-stone-200"}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://customer-assets.emergentagent.com/job_versailles-actu/artifacts/x061u1gg_logo%20Versailles%2B.jpeg";
            e.target.className = "w-full h-full object-contain p-12 bg-stone-200";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link */}
        <Link 
          to={`/categorie/${article.category}`}
          className="inline-flex items-center gap-2 font-inter text-sm text-stone-500 hover:text-red-800 transition-colors duration-200 mb-6"
          data-testid="article-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {categoryLabels[article.category] || article.category}
        </Link>

        {/* Category */}
        <span className="category-label text-red-800 block mb-4">
          {categoryLabels[article.category] || article.category}
        </span>

        {/* Title */}
        <h1 
          className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-6"
          data-testid="article-title"
        >
          {article.title}
        </h1>

        {/* Excerpt */}
        <p className="font-lora text-xl text-stone-600 leading-relaxed mb-8 border-l-4 border-red-800 pl-6">
          {article.excerpt}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 font-inter mb-8 pb-8 border-b border-stone-200">
          <span className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {article.author}
          </span>
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {formattedDate}
          </span>
          <span className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            {article.views} vues
          </span>
          {/* Archive badge for PDF articles */}
          {article.generation_type === 'pdf' && (article.journal_numero || article.journal_date) && (
            <span className="flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-sm font-medium">
              📰 Archives {article.journal_numero} {article.journal_date && `• ${article.journal_date}`}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="article-content text-lg text-stone-800" data-testid="article-content">
          {contentParagraphs.map((paragraph, index) => (
            <p key={index} className="mb-6 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Source */}
        {article.source && (
          <div className="mt-12 pt-8 border-t border-stone-200">
            <p className="font-inter text-sm text-stone-500">
              Source : {article.source}
              {article.source_url && (
                <a 
                  href={article.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-red-800 hover:text-red-900 inline-flex items-center gap-1"
                >
                  Lire l'article original
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
        )}

        {/* Generation info */}
        {article.generation_type && article.generation_type !== 'manual' && (
          <div className="mt-4 p-4 bg-stone-200 rounded-sm">
            <p className="font-inter text-xs text-stone-500">
              {article.generation_type === 'ai' 
                ? 'Cet article a été généré avec l\'assistance de l\'intelligence artificielle.'
                : article.generation_type === 'pdf'
                ? `Article extrait des archives VersaillesPlus${article.journal_numero ? ` (${article.journal_numero}` : ''}${article.journal_date ? ` - ${article.journal_date})` : article.journal_numero ? ')' : ''}.`
                : 'Article issu de la veille automatisée.'}
            </p>
          </div>
        )}
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-8">
              Articles similaires
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((relArticle) => (
                <ArticleCard key={relArticle.id} article={relArticle} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
    <Footer />
    </>
  );
}
